
import type React from "react"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { type Gym, getGym, updateGym } from "@/api/gyms"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

interface EditGymDialogProps {
  gymId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onGymUpdated?: () => void
}

export function EditGymDialog({ gymId, open, onOpenChange, onGymUpdated }: EditGymDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [gym, setGym] = useState<Gym | null>(null)
  const [formData, setFormData] = useState<Partial<Gym>>({
    name: "",
    address: "",
    city: "",
    country: "",
    phone: "",
    email: "",
    workingHours: "",
    facilities: "", // Changed from array to string
    status: "active",
  })

  useEffect(() => {
    if (open && gymId) {
      fetchGym()
    }
  }, [open, gymId])

  const fetchGym = async () => {
    setIsFetching(true)
    try {
      const data = await getGym(gymId)
      setGym(data)
      setFormData({
        name: data.name,
        address: data.address,
        city: data.city,
        country: data.country,
        phone: data.phone,
        email: data.email,
        workingHours: data.workingHours || "",
        facilities: data.facilities || "", // No need to join array
        status: data.status,
      })
    } catch (error) {
      console.error("Failed to fetch gym:", error)
      toast({
        title: t("common.error"),
        description: t("gyms.errorFetchingGym"),
        variant: "destructive",
      })
      onOpenChange(false)
    } finally {
      setIsFetching(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }))
  }

  const handleStatusChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      status: value as "active" | "inactive",
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await updateGym(gymId, formData) // This now uses PATCH instead of PUT
      toast({
        title: t("common.success"),
        description: t("gyms.gymUpdated"),
      })
      onOpenChange(false)
      if (onGymUpdated) {
        onGymUpdated()
      }
    } catch (error) {
      console.error("Failed to update gym:", error)
      toast({
        title: t("common.error"),
        description: t("gyms.errorUpdatingGym"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isFetching) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("gyms.editGym")}</DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">{t("common.loading")}</div>
        </DialogContent>
      </Dialog>
    )
  }

  if (!gym) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("gyms.editGym")}</DialogTitle>
          <DialogDescription>{t("gyms.editGymDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {t("gyms.name")}
              </Label>
              <Input id="name" value={formData.name} onChange={handleChange} className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="address" className="text-right">
                {t("gyms.address")}
              </Label>
              <Input id="address" value={formData.address} onChange={handleChange} className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="city" className="text-right">
                {t("gyms.city")}
              </Label>
              <Input id="city" value={formData.city} onChange={handleChange} className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="country" className="text-right">
                {t("gyms.country")}
              </Label>
              <Input id="country" value={formData.country} onChange={handleChange} className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                {t("gyms.phone")}
              </Label>
              <Input id="phone" value={formData.phone} onChange={handleChange} className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                {t("gyms.email")}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="workingHours" className="text-right">
                {t("gyms.workingHours")}
              </Label>
              <Input id="workingHours" value={formData.workingHours} onChange={handleChange} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="facilities" className="text-right">
                {t("gyms.facilities")}
              </Label>
              <Textarea
                id="facilities"
                value={formData.facilities || ""}
                onChange={handleChange}
                className="col-span-3"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                {t("gyms.status")}
              </Label>
              <Select value={formData.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder={t("gyms.selectStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("gyms.active")}</SelectItem>
                  <SelectItem value="inactive">{t("gyms.inactive")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("common.loading") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
